/**
 * The live hub: routes the socket's live messages to the screens that show
 * what changed (docs/live-sync/implementation-plan.md 4.6; the rules are
 * docs/notifications/implementation-guide.md 15.5).
 *
 * WHERE MESSAGES COME FROM
 *
 * There is ONE socket per tab - the bell's (notifications/channel.js), owned
 * by NotificationProvider. Besides { type: 'notification' } it now carries
 *
 *   { type: 'changed', changes: [{ topic, fundId?, ids? }, ...], at }
 *       something a screen shows changed and committed (live/topics.js on the
 *       server lists the topics); the screen re-reads through the REST API
 *   { type: 'session', reason, at }
 *       this person's access changed (suspended, MFA reset, roles changed):
 *       re-read who they are
 *
 * NotificationProvider hands every message to receive(), and tells the hub
 * when the socket (re)connects (connected()).
 *
 * THE RULES (each one is tested in hub.test.js)
 *
 *   1. Debounce per screen: a burst of hints - a checker approving 20 orders,
 *      a close run touching every fund - becomes ONE reload, DEBOUNCE_MS after
 *      the first hint of the burst.
 *   2. Reload only while the person is active (isActive: the same gate the
 *      business day's poll uses, businessDay.js pollGate). A reload is an API
 *      call, and the authorizer counts every call as activity: a hint must
 *      never keep an idle session signed in. A screen hinted while the person
 *      is away is marked stale instead, and reloads ONCE when they come back
 *      (activity()).
 *   3. After a RE-connect that followed a real outage (the socket down longer
 *      than RECONNECT_GRACE_MS), every subscribed screen reloads once: hints
 *      sent while it was down are gone. A routine reconnect - the server's
 *      15-minute recheck, API Gateway's 2-hour limit - is back within seconds,
 *      and the server delivers the hint that triggers a recheck before it
 *      closes (notifications/live.js deliver), so reloading every open screen
 *      each time would be calls with nothing changed. The first connect is not
 *      a reconnect - every screen has just made its own first read.
 *   4. A screen that names a fund hears only that fund's hints, and hints
 *      that name no fund (which may concern any fund).
 *   5. A reload that throws is the screen's business (it shows its own
 *      error): the hub swallows it so one screen cannot stop the others.
 *
 * Pure: the timers and the activity gate are injected, so the rules run in
 * node without a browser.
 */

/** The quiet period a burst of hints must leave before a screen reloads. */
export const DEBOUNCE_MS = 1500;

/**
 * Rule 3: a socket back within this long missed nothing worth re-reading every
 * screen for. A routine reconnect takes about 2 s (channel.js FIRST_BACKOFF_MS).
 */
export const RECONNECT_GRACE_MS = 30_000;

/**
 * @param {object} options
 * @param {() => boolean} options.isActive  whether the person is active now (pollGate's verdict)
 * @param {typeof setTimeout} [options.setTimer]
 * @param {typeof clearTimeout} [options.clearTimer]
 * @param {number} [options.debounceMs]
 */
export function createLiveHub({ isActive, setTimer = setTimeout, clearTimer = clearTimeout, debounceMs = DEBOUNCE_MS }) {
  /** Every mounted screen: { topics: Set<string>, fundId: string|null, reload: () => *, timer, stale } */
  const subscribers = new Set();
  /** Whoever wants session hints (LiveProvider: re-read the principal). */
  const sessionListeners = new Set();
  /** Whether the socket has opened before: rule 3 skips the first open. */
  let everConnected = false;

  /** Run a screen's reload now, or mark it stale while the person is away (rule 2). */
  function fire(sub) {
    if (!subscribers.has(sub)) return; // unmounted while its timer ran
    if (!isActive()) {
      sub.stale = true;
      return;
    }
    sub.stale = false;
    try {
      const out = sub.reload();
      // A reload that returns a promise and rejects: the screen shows its own error (rule 5).
      if (out && typeof out.catch === 'function') out.catch(() => {});
    } catch {
      // Rule 5: one screen's failure never stops the others.
    }
  }

  /** Rule 1: the first hint of a burst starts the timer; the rest of the burst ride on it. */
  function schedule(sub) {
    if (sub.timer !== null) return;
    sub.timer = setTimer(() => {
      sub.timer = null;
      fire(sub);
    }, debounceMs);
  }

  /** Whether one change concerns one screen (topic, then rule 4's fund). */
  function concerns(sub, change) {
    if (!sub.topics.has(change?.topic)) return false;
    if (!sub.fundId || !change.fundId) return true;
    return sub.fundId === change.fundId;
  }

  return {
    /**
     * A screen listens. Returns the function that stops it listening (on unmount).
     *
     * @param {{ topics: string[], fundId?: string|null, reload: () => * }} screen
     * @returns {() => void}
     */
    subscribe({ topics, fundId = null, reload }) {
      const sub = { topics: new Set(topics), fundId: fundId || null, reload, timer: null, stale: false };
      subscribers.add(sub);
      return () => {
        if (sub.timer !== null) clearTimer(sub.timer);
        subscribers.delete(sub);
      };
    },

    /**
     * Listen for session hints. Returns the function that stops listening.
     *
     * @param {(message: { reason: string }) => void} listener
     */
    onSession(listener) {
      sessionListeners.add(listener);
      return () => sessionListeners.delete(listener);
    },

    /** A message from the socket. Anything not live (a notification, junk) is ignored. */
    receive(message) {
      if (message?.type === 'changed' && Array.isArray(message.changes)) {
        for (const sub of subscribers) {
          if (message.changes.some((change) => concerns(sub, change))) schedule(sub);
        }
        return;
      }
      if (message?.type === 'session') {
        for (const listener of sessionListeners) {
          try {
            listener({ reason: typeof message.reason === 'string' ? message.reason : 'CHANGED' });
          } catch {
            // A listener's failure is its own.
          }
        }
      }
    },

    /**
     * The socket opened. Rule 3: after a reconnect that followed a real outage,
     * every screen reloads once.
     *
     * @param {{ downMs?: number|null }} [info] how long there was no socket (channel.js
     *   onConnected). Unknown means "assume an outage": a reload too many is only a read.
     */
    connected({ downMs } = {}) {
      const outage = typeof downMs !== 'number' || downMs > RECONNECT_GRACE_MS;
      if (everConnected && outage) {
        for (const sub of subscribers) schedule(sub);
      }
      everConnected = true;
    },

    /** The person did something (or the page came back): rule 2's stale screens reload once. */
    activity() {
      if (!isActive()) return;
      for (const sub of subscribers) {
        if (sub.stale && sub.timer === null) fire(sub);
      }
    },

    /** Stop everything: sign-out. Pending reloads are dropped. */
    stop() {
      for (const sub of subscribers) {
        if (sub.timer !== null) clearTimer(sub.timer);
      }
      subscribers.clear();
      sessionListeners.clear();
    },

    /** How many screens are listening (for the tests). */
    get size() {
      return subscribers.size;
    },
  };
}
